import dashboardAPI from './index'
import {
  daysBetween,
  formatYmd,
  monthsBetween,
  toLocalDate,
} from '@/core/utils/dashboardDates'
import { MIN_BLOCK_DURATION_MS, runWithMinDuration } from '@/core/utils/requestPacing'
import {
  normalizeAverageDailySeries,
  readDailyAverageValue,
} from '@/core/utils/dailyAverageFields'

/**
 * Progressive loading service for dashboard data
 * Loads data month by month for ranges larger than 15 days on desktop only
 */
class ProgressiveDashboardLoader {
  constructor() {
    this.isLoading = false
    this.currentProgress = 0
    this.totalMonths = 0
    this.onProgressCallback = null
    this.onCompleteCallback = null
    this.onErrorCallback = null
  }

  /**
   * Check if device is mobile
   */
  isMobileDevice() {
    // Check if device is mobile based on screen size and user agent
    const isMobileByScreen = window.innerWidth <= 768
    const isMobileByAgent =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )

    return isMobileByScreen || isMobileByAgent
  }

  /**
   * Calculate months between two dates
   */
  calculateMonthsBetween(startDate, endDate) {
    return monthsBetween(startDate, endDate)
  }

  /**
   * Calculate days between two dates
   */
  calculateDaysBetween(startDate, endDate) {
    return daysBetween(startDate, endDate)
  }

  /**
   * Generate monthly date ranges (fechas locales, sin toISOString para no correr un día por zona horaria)
   */
  generateMonthlyRanges(startDate, endDate) {
    const ranges = []
    const start = toLocalDate(startDate)
    const end = toLocalDate(endDate)
    if (!start || !end) return ranges

    let current = new Date(start.getFullYear(), start.getMonth(), 1)

    while (current <= end) {
      const monthStart = new Date(current)
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0)

      // Adjust for requested range
      const rangeStart = monthStart < start ? start : monthStart
      const rangeEnd = monthEnd > end ? end : monthEnd

      ranges.push({
        start: formatYmd(rangeStart),
        end: formatYmd(rangeEnd),
        month: current.getMonth() + 1,
        year: current.getFullYear(),
      })

      current.setMonth(current.getMonth() + 1)
    }

    return ranges
  }

  /**
   * Bloques mensuales: el backend devuelve en `year_data[].income` el total del rango pedido,
   * así que bloques más grandes repetirían ese total en cada mes de la gráfica.
   */
  getBlockRanges(startDate, endDate) {
    return this.generateMonthlyRanges(startDate, endDate)
  }

  /**
   * Load data for a specific month using getData
   */
  async loadMonthData(dateRange, facilities, userId = null, signal = null) {
    const data = {
      start_date: dateRange.start,
      end_date: dateRange.end,
      facilities,
    }

    if (userId) {
      data.user_id = userId
    }

    try {
      const response = await dashboardAPI.getData(data, { signal })
      return response.data
    } catch (error) {
      console.error(
        `Error loading data for ${dateRange.start} to ${dateRange.end}:`,
        error
      )
      throw error
    }
  }

  /**
   * Combine monthly data by concatenating arrays and summing totals
   */
  combineMonthlyData(monthlyDataArray, dateRange = null) {
    if (monthlyDataArray.length === 0) return {}
    if (monthlyDataArray.length === 1) {
      const result = monthlyDataArray[0]
      if (dateRange) {
        result.date_range = dateRange
      }
      this.normalizeInsBaseValue(result)
      return result
    }

    const combined = JSON.parse(JSON.stringify(monthlyDataArray[0]))

    for (let i = 1; i < monthlyDataArray.length; i++) {
      const monthData = monthlyDataArray[i]

      // 1. Concatenar arrays (excepto year_data que necesita lógica especial)
      ;[
        'all_patients',
        'patients',
        'patients_per_day',
        'patients_per_month',
      ].forEach(key => {
        if (combined[key] && monthData[key]) {
          combined[key] = combined[key].concat(monthData[key])
        }
      })

      // Manejar year_data de forma especial para combinar totals
      if (combined.year_data && monthData.year_data) {
        // Si existe un año/mes en monthData que ya existe en combined, combinar los totals
        monthData.year_data.forEach(newYearData => {
          const existingIndex = combined.year_data.findIndex(
            existing =>
              existing.year === newYearData.year && existing.month === newYearData.month
          )

          if (existingIndex !== -1) {
            // Combinar totals si ya existe el año/mes
            const existingTotals = combined.year_data[existingIndex].totals || {}
            const newTotals = newYearData.totals || {}

            // Combinar todos los tipos de visitas
            const combinedTotals = { ...existingTotals }
            Object.keys(newTotals).forEach(key => {
              combinedTotals[key] =
                (parseInt(combinedTotals[key]) || 0) + (parseInt(newTotals[key]) || 0)
            })

            combined.year_data[existingIndex].totals = combinedTotals
            combined.year_data[existingIndex].total =
              (parseInt(combined.year_data[existingIndex].total) || 0) +
              (parseInt(newYearData.total) || 0)
            combined.year_data[existingIndex].income =
              (parseInt(combined.year_data[existingIndex].income) || 0) +
              (parseInt(newYearData.income) || 0)
          } else {
            // Si no existe, agregarlo al array
            combined.year_data.push(newYearData)
          }
        })
      }

      // Concatenar arrays de promedios diarios
      const dailyArrays = [
        'total_daily',
        'nc_daily',
        'np_daily',
        'cv_daily',
        'fu_daily',
        'pi_daily',
        'pp_daily',
        'rs_daily',
      ]

      dailyArrays.forEach(key => {
        if (
          monthData.average &&
          monthData.average[key] &&
          Array.isArray(monthData.average[key])
        ) {
          if (!combined.average[key]) {
            combined.average[key] = []
          }
          combined.average[key] = combined.average[key].concat(monthData.average[key])
        }
      })

      // Combinar summary / contadores $30 (Σ total / Σ count entre bloques)
      if (combined.average && monthData.average) {
        this.mergeAverageSummary(combined.average, monthData.average)
        ;['np_30_count', 'nc_30_count'].forEach(key => {
          const before = parseInt(combined.average[key], 10) || 0
          const toAdd = parseInt(monthData.average[key], 10) || 0
          if (before || toAdd || combined.average[key] !== undefined) {
            combined.average[key] = before + toAdd
          }
        })
        if (monthData.average.by_type || combined.average.by_type) {
          combined.average.by_type = this.mergeAverageByType(
            combined.average.by_type,
            monthData.average.by_type
          )
        }
        if (
          monthData.average.np_nc_30_by_doctor_facility ||
          combined.average.np_nc_30_by_doctor_facility
        ) {
          combined.average.np_nc_30_by_doctor_facility = this.mergeCount30ByDoctor(
            combined.average.np_nc_30_by_doctor_facility,
            monthData.average.np_nc_30_by_doctor_facility
          )
        }
      }

      // Pacientes por tipo: sumar count por type_visits_id
      if (monthData.patients_by_type || combined.patients_by_type) {
        combined.patients_by_type = this.mergePatientsByType(
          combined.patients_by_type,
          monthData.patients_by_type
        )
      }

      // 2. Sumar totales
      if (combined.totals && monthData.totals) {
        ;['income', 'expenses', 'net_income'].forEach(k => {
          combined.totals[k] =
            (parseFloat(combined.totals[k]) || 0) + (parseFloat(monthData.totals[k]) || 0)
        })
      }
      if (combined.cash_cc && monthData.cash_cc) {
        ;['cash', 'cc'].forEach(k => {
          combined.cash_cc[k] =
            (parseFloat(combined.cash_cc[k]) || 0) +
            (parseFloat(monthData.cash_cc[k]) || 0)
        })
      }
      if (combined.ins_base_value || monthData.ins_base_value) {
        this.mergeInsBaseValue(combined, monthData)
      }
      if (combined.total_refunds !== undefined && monthData.total_refunds !== undefined) {
        combined.total_refunds =
          (parseFloat(combined.total_refunds) || 0) +
          (parseFloat(monthData.total_refunds) || 0)
      }

      // 3. Sumar memberships
      if (combined.memberships && monthData.memberships) {
        combined.memberships.NP =
          (parseInt(combined.memberships.NP) || 0) +
          (parseInt(monthData.memberships.NP) || 0)
        combined.memberships.NC =
          (parseInt(combined.memberships.NC) || 0) +
          (parseInt(monthData.memberships.NC) || 0)
        // Sumar por ubicación
        if (combined.memberships.data && monthData.memberships.data) {
          ;['NP', 'NC'].forEach(type => {
            if (combined.memberships.data[type] && monthData.memberships.data[type]) {
              Object.keys(monthData.memberships.data[type]).forEach(location => {
                if (!combined.memberships.data[type][location]) {
                  combined.memberships.data[type][location] = 0
                }
                combined.memberships.data[type][location] +=
                  parseInt(monthData.memberships.data[type][location]) || 0
              })
            }
          })
        }
      }

      // 6. Sumar stadistics/statistics si existen
      if (combined.stadistics && monthData.stadistics) {
        Object.keys(monthData.stadistics).forEach(key => {
          if (
            typeof combined.stadistics[key] === 'number' &&
            typeof monthData.stadistics[key] === 'number'
          ) {
            combined.stadistics[key] += monthData.stadistics[key]
          }
        })
      }
      if (combined.statistics && monthData.statistics) {
        Object.keys(monthData.statistics).forEach(key => {
          if (
            typeof combined.statistics[key] === 'number' &&
            typeof monthData.statistics[key] === 'number'
          ) {
            combined.statistics[key] += monthData.statistics[key]
          }
        })
      }

      // 5. Concatenar bonus (ya implementado arriba)
      if (combined.bonus && monthData.bonus) {
        Object.keys(monthData.bonus).forEach(location => {
          if (!combined.bonus[location]) {
            combined.bonus[location] = {}
          }
          Object.keys(monthData.bonus[location]).forEach(date => {
            if (!combined.bonus[location][date]) {
              combined.bonus[location][date] = JSON.parse(
                JSON.stringify(monthData.bonus[location][date])
              )
            } else {
              const existingDate = combined.bonus[location][date]
              const newDate = monthData.bonus[location][date]
              existingDate.total =
                (parseFloat(existingDate.total) || 0) + (parseFloat(newDate.total) || 0)
              existingDate.total_to_pay =
                (parseFloat(existingDate.total_to_pay) || 0) +
                (parseFloat(newDate.total_to_pay) || 0)
              if (existingDate.appointments && newDate.appointments) {
                existingDate.appointments = existingDate.appointments.concat(
                  newDate.appointments
                )
              } else if (newDate.appointments) {
                existingDate.appointments = newDate.appointments
              }
            }
          })
        })
      }
    }

    // Agregar el rango de fechas para el cálculo de promedios
    if (dateRange) {
      combined.date_range = dateRange
    }

    // Recalcular promedios correctamente después de combinar todos los datos
    this.recalculateAverages(combined)
    this.normalizeInsBaseValue(combined)

    return combined
  }

  mergeInsBaseValue(combined, monthData) {
    const cur = combined.ins_base_value || {
      count: 0,
      total: 0,
      np: { count: 0, total: 0 },
      nc: { count: 0, total: 0 },
    }
    const add = monthData.ins_base_value || {}
    combined.ins_base_value = {
      count: (parseInt(cur.count, 10) || 0) + (parseInt(add.count, 10) || 0),
      total: (parseFloat(cur.total) || 0) + (parseFloat(add.total) || 0),
      np: {
        count: (parseInt(cur.np?.count, 10) || 0) + (parseInt(add.np?.count, 10) || 0),
        total: (parseFloat(cur.np?.total) || 0) + (parseFloat(add.np?.total) || 0),
      },
      nc: {
        count: (parseInt(cur.nc?.count, 10) || 0) + (parseInt(add.nc?.count, 10) || 0),
        total: (parseFloat(cur.nc?.total) || 0) + (parseFloat(add.nc?.total) || 0),
      },
    }
  }

  normalizeInsBaseValue(target) {
    try {
      if (!target || typeof target !== 'object' || !target.ins_base_value) return
      const v = target.ins_base_value
      target.ins_base_value = {
        count: parseInt(v.count, 10) || 0,
        total: parseFloat(v.total) || 0,
        np: {
          count: parseInt(v.np?.count, 10) || 0,
          total: parseFloat(v.np?.total) || 0,
        },
        nc: {
          count: parseInt(v.nc?.count, 10) || 0,
          total: parseFloat(v.nc?.total) || 0,
        },
      }
    } catch (e) {}
  }

  mergeSummaryBucket(a = {}, b = {}) {
    const count = (parseInt(a.count, 10) || 0) + (parseInt(b.count, 10) || 0)
    const total = (parseFloat(a.total) || 0) + (parseFloat(b.total) || 0)
    return {
      count,
      total,
      average: count > 0 ? Math.round((total / count) * 100) / 100 : 0,
    }
  }

  /** Suma patients_by_type[].count por type_visits_id. */
  mergePatientsByType(existing = [], incoming = []) {
    const map = new Map()
    ;[...(existing || []), ...(incoming || [])].forEach(item => {
      if (!item) return
      const key =
        item.type_visits_id != null
          ? `id:${item.type_visits_id}`
          : `p:${item.prefix || ''}`
      const prev = map.get(key)
      const count = parseInt(item.count, 10) || 0
      if (!prev) {
        map.set(key, {
          type_visits_id: item.type_visits_id,
          prefix: item.prefix,
          name: item.name,
          count,
        })
      } else {
        prev.count += count
        if (!prev.prefix && item.prefix) prev.prefix = item.prefix
        if (!prev.name && item.name) prev.name = item.name
      }
    })
    return Array.from(map.values())
  }

  /**
   * Combina average.by_type por type_visits_id (Σ total / Σ count).
   * El average crudo incluye las visitas en $30.
   */
  mergeAverageByType(existing = [], incoming = []) {
    const map = new Map()
    ;[...(existing || []), ...(incoming || [])].forEach(item => {
      if (!item) return
      const key =
        item.type_visits_id != null
          ? `id:${item.type_visits_id}`
          : `p:${item.prefix || ''}`
      const prev = map.get(key)
      const count = parseInt(item.count, 10) || 0
      const total = parseFloat(item.total) || 0
      if (!prev) {
        map.set(key, {
          type_visits_id: item.type_visits_id,
          prefix: item.prefix,
          name: item.name,
          count,
          total,
          average: count > 0 ? Math.round((total / count) * 100) / 100 : 0,
        })
      } else {
        prev.count += count
        prev.total += total
        prev.average =
          prev.count > 0 ? Math.round((prev.total / prev.count) * 100) / 100 : 0
        if (!prev.prefix && item.prefix) prev.prefix = item.prefix
        if (!prev.name && item.name) prev.name = item.name
      }
    })
    return Array.from(map.values())
  }

  mergeAverageSummary(targetAverage, sourceAverage) {
    if (!targetAverage || !sourceAverage?.summary) return
    if (!targetAverage.summary) {
      targetAverage.summary = {
        np: { average: 0, count: 0, total: 0 },
        nc: { average: 0, count: 0, total: 0 },
        membership: { average: 0, count: 0, total: 0 },
      }
    }
    ;['np', 'nc', 'membership'].forEach(key => {
      if (!sourceAverage.summary[key] && !targetAverage.summary[key]) return
      targetAverage.summary[key] = this.mergeSummaryBucket(
        targetAverage.summary[key],
        sourceAverage.summary[key]
      )
    })
  }

  mergeCount30ByDoctor(existing = [], incoming = []) {
    const map = new Map()
    ;[...(existing || []), ...(incoming || [])].forEach(item => {
      if (!item) return
      const key = `${item.doctor_id}|${item.facility_id}`
      const prev = map.get(key)
      const np = parseInt(item.np_30_count, 10) || 0
      const nc = parseInt(item.nc_30_count, 10) || 0
      if (!prev) {
        map.set(key, {
          doctor_id: item.doctor_id,
          doctor_name: item.doctor_name || '',
          facility_id: item.facility_id,
          facility_name: item.facility_name || '',
          np_30_count: np,
          nc_30_count: nc,
        })
        return
      }
      prev.np_30_count += np
      prev.nc_30_count += nc
      if (!prev.doctor_name && item.doctor_name) prev.doctor_name = item.doctor_name
      if (!prev.facility_name && item.facility_name) prev.facility_name = item.facility_name
    })
    return Array.from(map.values())
  }

  /**
   * Aplica average.total / nc / np desde average.summary (Σ total / Σ count).
   * Nunca promedia promedios diarios. Devuelve true si usó summary.
   */
  applySummaryAverages(average) {
    const summary = average?.summary
    if (!summary || typeof summary !== 'object') return false
    const hasSummary = ['nc', 'np'].some(
      key => summary[key] && (summary[key].count != null || summary[key].total != null)
    )
    if (!hasSummary) return false

    ;['nc', 'np'].forEach(key => {
      const bucket = summary[key]
      if (!bucket) return
      const count = parseInt(bucket.count, 10) || 0
      const total = parseFloat(bucket.total) || 0
      const avg =
        bucket.average != null && bucket.average !== ''
          ? parseFloat(bucket.average)
          : count > 0
          ? total / count
          : 0
      average[key] = Math.trunc(avg || 0)
    })

    if (average.nc != null && average.np != null) {
      average.nc_np = Math.round((average.nc + average.np) / 2)
    }
    return true
  }

  /**
   * Recalcular promedios basado en los datos combinados
   * Similar a la lógica del backend getValuesTotals.php
   */
  recalculateAverages(combinedData) {
    if (!combinedData.average) return

    // Calcular diferencia de días entre el rango total
    const diffDays = Math.max(
      1,
      daysBetween(combinedData.date_range?.start, combinedData.date_range?.end) + 1
    )

    // Contar pacientes totales
    const totalPatients = combinedData.all_patients?.length || 1

    // Recalcular promedios basado en totals y expenses
    if (combinedData.totals) {
      const totalIncome = parseFloat(combinedData.totals.income) || 0
      const totalExpenses = parseFloat(combinedData.totals.expenses) || 0
      const totalRefunds = parseFloat(combinedData.total_refunds) || 0

      // Promedios por día (como en el backend)
      combinedData.average.income_days = Math.round(totalIncome / diffDays)
      combinedData.average.expenses_days = Math.round(totalExpenses / diffDays)
      combinedData.average.net_income_days = Math.round(
        (totalIncome - totalExpenses - totalRefunds) / diffDays
      )

      // Promedios por paciente (como en el backend)
      combinedData.average.income_day = Math.round(totalIncome / totalPatients)
      combinedData.average.expenses_day = Math.round(totalExpenses / totalPatients)
      combinedData.average.net_income_day = Math.round(
        (totalIncome - totalExpenses - totalRefunds) / totalPatients
      )
    }

    // Preferir summary (Σ total / Σ count). Fallback diario solo si el backend aún no envía summary.
    const usedSummary = this.applySummaryAverages(combinedData.average)
    if (!usedSummary) {
      if (combinedData.average.nc_daily && combinedData.average.nc_daily.length > 0) {
        const ncSum = combinedData.average.nc_daily.reduce(
          (sum, item) => sum + parseFloat(item.AVG_NC || 0),
          0
        )
        combinedData.average.nc = Math.round(ncSum / combinedData.average.nc_daily.length)
      }

      if (combinedData.average.np_daily && combinedData.average.np_daily.length > 0) {
        const npSum = combinedData.average.np_daily.reduce(
          (sum, item) => sum + parseFloat(item.AVG_NP || 0),
          0
        )
        combinedData.average.np = Math.round(npSum / combinedData.average.np_daily.length)
      }
    }

    // A9: leer alias correctos; fallback AVG_NP en series renombradas
    normalizeAverageDailySeries(combinedData.average)
    const avgFromDaily = (listKey, fieldName, outKey) => {
      const list = combinedData.average[listKey]
      if (!list || !list.length) return
      const sum = list.reduce(
        (acc, item) => acc + readDailyAverageValue(item, fieldName),
        0
      )
      combinedData.average[outKey] = Math.round(sum / list.length)
    }
    avgFromDaily('cv_daily', 'AVG_CV', 'cv')
    avgFromDaily('fu_daily', 'AVG_FU', 'fu')
    avgFromDaily('pi_daily', 'AVG_PI', 'pi')
    avgFromDaily('pp_daily', 'AVG_PP', 'pp')
    avgFromDaily('rs_daily', 'AVG_RS', 'rs')

    // Calcular nc_np promedio
    if (combinedData.average.nc && combinedData.average.np) {
      combinedData.average.nc_np = Math.round(
        (combinedData.average.nc + combinedData.average.np) / 2
      )
    }
  }

  /**
   * Progressive data loading - only for desktop devices with large date ranges
   */
  async loadProgressiveData(
    { start, end, facilities },
    userId = null,
    showModal = false,
    signal = null
  ) {
    // Check if device is mobile
    if (this.isMobileDevice()) {
      console.log('Progressive loading disabled for mobile devices')
      // Fall back to normal loading for mobile
      const data = {
        start_date: start,
        end_date: end,
        facilities,
      }
      if (userId) {
        data.user_id = userId
      }
      const response = await dashboardAPI.getData(data, { signal })
      return response.data
    }

    const daysBetween = this.calculateDaysBetween(start, end)

    // Use normal loading for 15 days or less
    if (daysBetween <= 15) {
      const data = {
        start_date: start,
        end_date: end,
        facilities,
      }
      if (userId) {
        data.user_id = userId
      }
      const response = await dashboardAPI.getData(data, { signal })
      return response.data
    }

    // Use progressive loading for more than 15 days on desktop only
    this.isLoading = true
    this.currentProgress = 0

    try {
      const monthlyRanges = this.getBlockRanges(start, end)
      this.totalMonths = monthlyRanges.length
      const monthlyDataArray = []

      for (let i = 0; i < monthlyRanges.length; i++) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        const range = monthlyRanges[i]

        // Update progress only if modal should be shown
        this.currentProgress = i + 1
        if (this.onProgressCallback && showModal) {
          this.onProgressCallback({
            current: this.currentProgress,
            total: this.totalMonths,
            percentage: Math.round((this.currentProgress / this.totalMonths) * 100),
            currentMonth: range.month,
            currentYear: range.year,
          })
        }

        const isLastMonth = i === monthlyRanges.length - 1
        const monthData = await runWithMinDuration(
          () => this.loadMonthData(range, facilities, userId, signal),
          isLastMonth ? 0 : MIN_BLOCK_DURATION_MS
        )
        monthlyDataArray.push(monthData)
      }

      // Combine all data
      const combinedData = this.combineMonthlyData(monthlyDataArray, { start, end })

      this.isLoading = false
      if (this.onCompleteCallback && showModal) {
        this.onCompleteCallback(combinedData)
      }

      return combinedData
    } catch (error) {
      this.isLoading = false
      if (this.onErrorCallback && showModal) {
        this.onErrorCallback(error)
      }
      throw error
    }
  }

  /**
   * Set up callbacks
   */
  onProgress(callback) {
    this.onProgressCallback = callback
  }

  onComplete(callback) {
    this.onCompleteCallback = callback
  }

  onError(callback) {
    this.onErrorCallback = callback
  }

  /**
   * Get current loading state
   */
  getLoadingState() {
    return {
      isLoading: this.isLoading,
      currentProgress: this.currentProgress,
      totalMonths: this.totalMonths,
      percentage:
        this.totalMonths > 0
          ? Math.round((this.currentProgress / this.totalMonths) * 100)
          : 0,
    }
  }
}

// Singleton instance
const progressiveLoader = new ProgressiveDashboardLoader()

export default progressiveLoader
